import PropTypes from 'prop-types'
import styled from 'styled-components'
import { NavLink } from 'react-router-dom'

import { Box, Container, media } from '@redriverpress/system'

const PillWrapper = styled(Box).attrs(() => ({
  as: 'nav',
  display: 'inline-flex',
  justifyContent: 'center',
  bg: 'white',
  border: 'solid 2px',
  borderColor: 'blue500',
  borderRadius: '2rem',
  boxShadow: 3,
  width: 1,
}))`
  pointer-events: none;
  &:hover {
    ${media.md`
      @media (pointer: none), (pointer: coarse) {
        background-color: ${props => props.theme.colors.white};
      }
      background-color: ${props => props.theme.colors.blue100};
    `}
  }
`

const Pill = styled(NavLink)`
  pointer-events: auto;
  background-image: none;

  &:nth-child(odd) {
    margin-right: 3px;
  }
  &:nth-child(even) {
    margin-left: 3px;
  }

  .pill {
    background-color: ${props => props.theme.colors.white};
    color: ${props => props.theme.colors.black};
    font-size: ${props => props.theme.fontSizes[3]};
    font-weight: '600';
    border-radius: 2rem;

    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background-color: ${props => props.theme.colors.blue100};
      color: ${props => props.theme.colors.black};
    }

    ${media.sm`
      font-size: ${props => props.theme.fontSizes[1]};
    `}
  }

  &.active {
    pointer-events: none;
  }

  &.active > .pill {
    background-color: ${props => props.theme.colors.blue500};
    color: ${props => props.theme.colors.white};
  }

  :focus {
    outline: none;
  }
  :focus-visible {
    box-shadow: 0px 0px 0px 2px #5774fc, 0px 0px 0px 4px #fff,
      0px 0px 0px 6px #5774fc;
    background: ${props => props.theme.colors.blue500};
  }
`

// Some tabs use objects for their `to`
// and some use React nodes for their `label`,
// so generating a key without relying on the index gets interesting…
export const getTabKey = tab => {
  if (typeof tab.to === 'string') return tab.to
  if (typeof tab.label === 'string') return tab.label
  return JSON.stringify(tab.to)
}

export const TabsPropType = PropTypes.arrayOf(
  PropTypes.shape({
    // Expects a React Router location prop, see:
    // https://reactrouter.com/web/api/NavLink
    to: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.object,
      PropTypes.string,
    ]).isRequired,
    exact: PropTypes.bool,
    // Prefer string labels where possible; if you need to pass a node,
    // ensure the wrapping component is a React Fragment in order to preserve
    // the effects of the grid layout on the grid container's immediate children.
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    // currently only showing on $sm and $md
    icon: PropTypes.string,
  })
)

export default function PillTabs({ tabs }) {
  return (
    <Container>
      <PillWrapper>
        {tabs.map(tab => (
          <Pill
            exact={tab.exact}
            to={tab.to}
            className={isActive => (isActive ? 'active' : 'inactive')}
            key={getTabKey(tab)}
            style={{ flex: 1 }}
          >
            <Box py={2} className='pill'>
              {tab.label}
            </Box>
          </Pill>
        ))}
      </PillWrapper>
    </Container>
  )
}

PillTabs.propTypes = {
  tabs: TabsPropType.isRequired,
}
